import React, { useEffect, useState, useRef } from "react";
import { Box, Card, Typography, Skeleton, Button, Avatar, IconButton } from "@mui/material";
import { Building, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CompanyHoverCard from "./CompanyHoverCard";
import axios from "axios";

const ActiveCompanies = ({ currentUserId }) => {
  const [companies, setCompanies] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  
  const [hoveredCompany, setHoveredCompany] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
  };

  useEffect(() => {
    fetch("https://apiweb.hitpoly.com/ajax/communityWidgetsController.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getActiveCompanies" })
    })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data && d.data.length > 0) {
          setCompanies(d.data);
        } else {
          setCompanies([
            { id: 'c1', name: 'Tech Solutions SA', logo: '', industry: 'Tecnología' },
            { id: 'c2', name: 'Global Marketing', logo: '', industry: 'Publicidad' },
            { id: 'c3', name: 'Hitpoly Digital Group', logo: '', industry: 'Software' }
          ]);
        }
      })
      .catch(() => {
        setCompanies([
          { id: 'c1', name: 'Tech Solutions SA', logo: '', industry: 'Tecnología' },
          { id: 'c2', name: 'Global Marketing', logo: '', industry: 'Publicidad' },
          { id: 'c3', name: 'Hitpoly Digital Group', logo: '', industry: 'Software' }
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleNavigateCompany = (companyId) => {
    navigate(`/systems/bolsa/empresa/${companyId}?userId=${currentUserId}`);
  };

  const handleMouseEnter = (e, company) => {
    clearTimeout(hoverTimeoutRef.current);
    const target = e.currentTarget;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverAnchor(target);
      setHoveredCompany(company);
    }, 250);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverAnchor(null);
      setHoveredCompany(null);
    }, 250);
  };

  const handleToggleFollow = async (e, companyId) => {
    e.stopPropagation();
    if (!currentUserId) return;
    try {
      const res = await axios.post("https://apiweb.hitpoly.com/ajax/bolsaController.php", {
        accion: "toggleFollowCompany",
        empresa_id: companyId,
        user_id: currentUserId
      });
      if (res.data?.success) {
        // Optimistic UI update or let hover card handle it since hover card syncs with cache
        // For ActiveCompanies we might just let the user see the change when they open the card or reload
      }
    } catch (e) {}
  };

  if (loading) return <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 4 }} />;
  if (!companies || companies.length === 0) return null;

  return (
    <Card sx={{ 
      bgcolor: "rgba(255,255,255,0.9)", 
      backdropFilter: "blur(8px)", 
      borderRadius: 4,
      border: '1px solid #f0f0f0',
      boxShadow: 'none',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: 300
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1a1a1a' }}>
          <Building size={18} color="#0a66c2" /> Empresas
          <Box sx={{ bgcolor: '#eef3f8', color: '#0a66c2', fontSize: '0.75rem', fontWeight: 'bold', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', ml: 0.5 }}>
            {companies.length}
          </Box>
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={scrollLeft} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronLeft size={16} /></IconButton>
          <IconButton size="small" onClick={scrollRight} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronRight size={16} /></IconButton>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ 
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto', className: 'community-wall-scroll', scrollbarWidth: 'thin', scrollbarColor: '#0B8DB5 transparent', 
        overflowY: 'hidden',
        pb: 1.5,
        mx: -0.5,
        px: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#0B8DB5', borderRadius: 4, border: 0 }
      }}>
        {companies.map(company => (
          <Card 
            key={company.id} 
            sx={{ 
              minWidth: 160, 
              maxWidth: 160,
              p: 2, 
              borderRadius: 3, 
              border: '1px solid #eef3f8',
              boxShadow: 'none',
              bgcolor: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              '&:hover': {
                borderColor: '#0a66c2',
                boxShadow: '0 4px 12px rgba(10, 102, 194, 0.08)'
              }
            }}
            onClick={() => handleNavigateCompany(company.id)}
            onMouseEnter={(e) => handleMouseEnter(e, company)}
            onMouseLeave={handleMouseLeave}
          >
            <Avatar 
              src={company.logo || "/images/default-company.png"} 
              variant="rounded" 
              sx={{ width: 56, height: 56, mb: 1.5, border: '1px solid #f0f0f0' }} 
            />
            <Typography variant="body2" fontWeight="800" color="#1a1a1a" sx={{ mb: 0.5, lineHeight: 1.2 }}> 
              {company.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ mb: 1, lineHeight: 1.3, overflow: 'visible' }}> 
              {company.industry || "Empresa activa"}
            </Typography>
          </Card>
        ))}
      </Box>

      {hoveredCompany && (
        <CompanyHoverCard 
          company={hoveredCompany}
          anchorEl={hoverAnchor}
          currentUserId={currentUserId}
          onClose={() => { setHoverAnchor(null); setHoveredCompany(null); }}
          onMouseEnter={() => clearTimeout(hoverTimeoutRef.current)}
          onMouseLeave={handleMouseLeave}
          onNavigateCompany={handleNavigateCompany}
        />
      )}

        <Button 
          onClick={() => navigate('/red-empresas')}
          sx={{ 
            width: '100%', 
            mt: 1, 
            textTransform: 'none', 
            fontWeight: '700',
            color: '#0a66c2',
            '&:hover': { bgcolor: '#eef3f8' }
          }}
          endIcon={<ArrowRight size={16} />}
        >
          Ver todas las empresas
      </Button>
    </Card>
  );
};

export default ActiveCompanies;


